import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { NgbModal, NgbPaginationModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { ReadService } from '@common/service/read.service';
import { AlertErrorService } from '@core/service/alert-error.service';
import { ConfirmDialogComponent } from '@common/dialog/confirm-dialog/confirm-dialog.component';
import { Pagination } from '@common/const/search-filter.constants';
import { TmwFilterPipe } from '@shared/pipes/filter.pipe';
import TmwDateTimePipe from '@shared/pipes/datetime.pipe';
import { SearchErrorMessageComponent } from '@shared/components/search-errorMessage/search-errorMessage.component';
import { TmwTranslateDirective } from '@shared/directives/translate.directive';
import { environment } from '@environments/environment';
import { AuthService } from '@core/auth/auth.service';

@Component({
  standalone: true,
  selector: 'tmw-audit-history',
  templateUrl: './audit-history.component.html',
  styleUrls: ['./audit-history.component.scss'],
  imports: [CommonModule, FormsModule, NgbPaginationModule, NgbTooltipModule, TmwFilterPipe, TmwDateTimePipe, TmwTranslateDirective, SearchErrorMessageComponent],
})
export class AuditHistoryComponent implements OnChanges {
  private readonly readService = inject(ReadService);
  private readonly modalService = inject(NgbModal);
  private readonly http = inject(HttpClient);
  private readonly alertService = inject(AlertErrorService);
  private readonly authService = inject(AuthService);

  /** Code de la requête nommée, ex: 'AuditHistory_Regle' */
  @Input() queryCode!: string;
  /** Identifiant de l'entité dont on veut voir l'historique */
  @Input() recordId!: number;
  /** Code d'accès requis pour afficher le bouton Revert (ex: 'roles_Update'). Si non fourni, le bouton reste visible (comportement historique). */
  @Input() accessCode?: string;
  /** Émis après chaque revert réussi (permet au parent de rafraîchir ses données) */
  @Output() revertSuccess = new EventEmitter<void>();

  elements: any[] = [];
  page = Pagination.PageStart;
  pageSize = Pagination.PageSize;
  totalCount = 0;
  selectedElement: any = null;
  comparisonRows: { key: string; oldVal: string; newVal: string; changed: boolean }[] = [];
  rawOldData: string | null = null;
  rawNewData: string | null = null;
  jsonParseError = false;
  filtrer = false;
  term = '';
  revertLoading = false;
  /** auditId de la ligne en cours de revert depuis le tableau (null si aucun) */
  revertingId: number | null = null;

  /* ── Tooltip custom (fiable dans les tables, indépendant de ng-bootstrap) ── */
  tmwTooltipText = '';
  tmwTooltipX = 0;
  tmwTooltipY = 0;
  tmwTooltipVisible = false;

  showTmwTooltip(event: MouseEvent, text: string): void {
    this.tmwTooltipText = text;
    this.tmwTooltipX = event.clientX;
    this.tmwTooltipY = event.clientY;
    this.tmwTooltipVisible = true;
  }

  moveTmwTooltip(event: MouseEvent): void {
    this.tmwTooltipX = event.clientX;
    this.tmwTooltipY = event.clientY;
  }

  hideTmwTooltip(): void {
    this.tmwTooltipVisible = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['queryCode'] || changes['recordId']) && this.queryCode && this.recordId) {
      this.search();
    }
  }

  search(): void {
    this.page = Pagination.PageStart;
    this.filter();
  }

  filter(): void {
    this.readService
      .queryPaged(this.queryCode, {
        params: { id: this.recordId },
        page: {
          page: this.page - 1,
          size: this.pageSize,
        },
      })
      .subscribe({
        next: (result) => {
          this.elements = result?.records ?? [];
          this.totalCount = result?.totalCount ?? 0;
        },
      });
  }

  openDetail(modal: TemplateRef<any>, element: any): void {
    this.selectedElement = element;
    this.comparisonRows = this.buildComparisonRows(element);
    this.revertLoading = false;
    this.modalService.open(modal, { size: 'xl', scrollable: true, centered: true, windowClass: 'tmw-audit-modal' });
  }

  /**
   * Indique si le bouton de revert doit être affiché pour l'opération donnée.
   * On ne propose pas de revert sur des opérations déjà de type REVERT.
   * Annuler un INSERT supprime physiquement la ligne (ticket 0005976) : interdit sur les
   * tables dont la création a des effets hors de la table (Keycloak, Camunda, MinIO, JobRunr)
   * ou qui ont des enfants. Table inconnue => interdit par prudence.
   */
  canRevert(operation: string, tableName?: string): boolean {
    if (!operation || operation.startsWith('REVERT')) return false;
    if (operation !== 'INSERT') return true;
    return !!tableName && !AuditHistoryComponent.TABLES_SANS_REVERT_CREATION.has(tableName.toUpperCase());
  }

  /** Tables dont la création ne peut pas être annulée (cf. canRevert). */
  private static readonly TABLES_SANS_REVERT_CREATION = new Set<string>([
    // Effets externes non défaits par le revert
    'USR_UTILISATEUR',    // compte Keycloak
    'WF_PROCESSUS',       // déploiement BPMN, fichier MinIO, STK_STOCKAGE
    'EDT_EDITION',        // fichier MinIO, STK_STOCKAGE, historique
    'PLN_PLANIFICATION',  // job JobRunr
    // Tables parentes (FK NO ACTION : le DELETE échoue ou laisse des orphelins)
    'DRL_REGLE',
    'DRL_GROUPE_REGLE',
    'WF_PROCESSUS_TACHE',
    'USR_ROLE',
    'REF_REQUETE',
    'REF_REFERENTIEL',
    'REF_DETAIL_REFERENTIEL',
    'REF_PRODUIT',
    'REF_MATRICE_DELEGATION',
    'CHG_ANNEXE_CONTEXT',
    'PLN_TACHE',
    'NTF_NOTIFICATION_CONTEXT',
  ]);

  /**
   * Indique si le bouton de revert doit être affiché : règle métier canRevert
   * ET, si un accessCode a été fourni par le composant hôte, droit d'accès de l'utilisateur.
   * Sans accessCode fourni, le comportement historique (toujours visible) est conservé.
   */
  canShowRevert(operation: string, tableName?: string): boolean {
    return this.canRevert(operation, tableName) &&(!this.accessCode || this.authService.hasAnyAccess([this.accessCode]));
  }

  /**
   * Revert déclenché directement depuis la ligne du tableau (sans ouvrir la modal).
   */
  revertFromList(element: any): void {
    const auditId: number = element['auditId'];
    const op: string = element['operation'] ?? 'cette modification';
    const confirmRef = this.openConfirmRevert(op, auditId);
    confirmRef.result
      .then((result) => {
        if (result !== 'confirmed') return;
        this.revertingId = auditId;
        this.executeRevert(auditId, () => {
          this.revertingId = null;
          this.filter();
          this.revertSuccess.emit();
        }, () => { this.revertingId = null; });
      })
      .catch(() => { /* annulé */ });
  }

  /**
   * Annule l'opération d'audit sélectionnée et restaure l'état précédent.
   * Ouvre une modale de confirmation avant d'exécuter.
   */
  revert(auditId: number, detailModal: any): void {
    const op = this.selectedElement?.['operation'] ?? 'cette modification';
    const confirmRef = this.openConfirmRevert(op, auditId);
    confirmRef.result
      .then((result) => {
        if (result !== 'confirmed') return;
        this.revertLoading = true;
        this.executeRevert(auditId, () => {
          this.revertLoading = false;
          detailModal.dismiss();
          this.filter();
          this.revertSuccess.emit();
        }, () => { this.revertLoading = false; });
      })
      .catch(() => { /* annulé */ });
  }

  /** Ouvre la modale de confirmation de revert (factory commune). */
  private openConfirmRevert(operation: string, auditId: number) {
    const ref = this.modalService.open(ConfirmDialogComponent, {
      size: 'md',
      backdrop: 'static',
      windowClass: 'modal show',
    });
    ref.componentInstance.title = "Confirmer l'annulation";
    ref.componentInstance.message =
      `Voulez-vous vraiment annuler l'opération "${this.operationLabel(operation)}" (audit #${auditId}) ?` +
      ` L'enregistrement reprendra les valeurs qu'il avait juste avant cette opération.` +
      ` Les modifications faites depuis ne sont pas annulées.`;
    ref.componentInstance.confirmLabel = 'Annuler cette opération';
    ref.componentInstance.confirmClass = 'btn-warning';
    ref.componentInstance.icon = 'bi-arrow-counterclockwise';
    return ref;
  }

  /** Exécute l'appel HTTP de revert et gère le résultat. */
  private executeRevert(auditId: number, onSuccess: () => void, onError: () => void): void {
    const url = `${environment.SERVER_READ_API_URL}/api/audit/${auditId}/revert?queryCode=${encodeURIComponent(this.queryCode)}`;
    this.http.put<{ revertAuditId: number }>(url, null).subscribe({
      next: () => {
        this.alertService.showSuccess('Revert effectué avec succès.', 'Succès');
        onSuccess();
      },
      error: (err) => {
        console.error('Erreur revert audit', err);
        this.alertService.showError('Une erreur est survenue lors du revert. Consultez les logs pour plus de détails.', 'Erreur');
        onError();
      },
    });
  }

  operationLabel(operation: string): string {
    const map: Record<string, string> = {
      INSERT:         'Création',
      UPDATE:         'Modification',
      DELETE:         'Suppression',
      REVERT_INSERT:  'Annulation création',
      REVERT_UPDATE:  'Annulation modification',
      REVERT_DELETE:  'Annulation suppression',
    };
    return map[operation] ?? operation;
  }

  badgeClass(operation: string): string {
    if (!operation) return 'bg-secondary';
    if (operation.startsWith('REVERT')) return 'bg-info text-dark';
    const map: Record<string, string> = {
      INSERT: 'bg-success',
      UPDATE: 'bg-warning text-dark',
      DELETE: 'bg-danger',
    };
    return map[operation] ?? 'bg-secondary';
  }

  private buildComparisonRows(element: any): { key: string; oldVal: string; newVal: string; changed: boolean }[] {
    const oldRaw: string | null = element['oldData'] ?? null;
    const newRaw: string | null = element['newData'] ?? null;
    this.rawOldData = oldRaw;
    this.rawNewData = newRaw;

    const oldObj = this.tryParse(oldRaw);
    const newObj = this.tryParse(newRaw);

    // Détecte si le JSON est malformé (données présentes mais non parsables)
    this.jsonParseError = (!oldObj && !!oldRaw) || (!newObj && !!newRaw);

    const changedSet = new Set(
      (element['changedColumns'] || '').split(',').map((c: string) => c.trim()).filter(Boolean)
    );
    const keys = [...new Set([...Object.keys(oldObj ?? {}), ...Object.keys(newObj ?? {})])];
    return keys.map(key => ({
      key,
      oldVal: this.formatVal(oldObj?.[key]),
      newVal: this.formatVal(newObj?.[key]),
      changed: changedSet.has(key),
    }));
  }

  private tryParse(raw: string | null | undefined): Record<string, any> | null {
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { /* continue */ }
    // Fallback : extraction heuristique quand le JSON contient des guillemets
    // non échappés dans les valeurs CLOB (ex: code Groovy dans REGLE).
    // Fonctionne car les clés Oracle sont toujours [A-Z_][A-Z_0-9]* sans car. spéciaux.
    return this.extractByColumnBoundaries(raw);
  }

  /**
   * Extrait les paires clé/valeur d'un JSON invalide en repérant les frontières
   * des clés Oracle (pattern `"NOM_COLONNE":`).
   * La valeur de chaque clé = tout ce qui se trouve entre la fin de cette clé
   * et le début de la clé suivante (ou la dernière accolade fermante).
   */
  private extractByColumnBoundaries(raw: string): Record<string, any> | null {
    const result: Record<string, any> = {};
    const keyPattern = /"([A-Z_][A-Z_0-9]*)":/g;
    const keys: { name: string; index: number; valueStart: number }[] = [];
    let m: RegExpExecArray | null;
    while ((m = keyPattern.exec(raw)) !== null) {
      keys.push({ name: m[1], index: m.index, valueStart: m.index + m[0].length });
    }
    if (keys.length === 0) return null;

    for (let i = 0; i < keys.length; i++) {
      const { name, valueStart } = keys[i];
      const valueEnd = i < keys.length - 1
        ? raw.lastIndexOf(',', keys[i + 1].index - 1)
        : raw.lastIndexOf('}');
      if (valueEnd <= valueStart) continue;
      let value = raw.substring(valueStart, valueEnd).trim();
      if (value === 'null') {
        result[name] = null;
      } else if (value.startsWith('"')) {
        // Supprime le premier et le dernier guillemet
        result[name] = value.endsWith('"') ? value.slice(1, -1) : value.slice(1);
      } else {
        result[name] = value;
      }
    }
    return Object.keys(result).length > 0 ? result : null;
  }

  private formatVal(val: any): string {
    if (val === null || val === undefined) return '';
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
  }
}
